import { REVAMP_API } from '../../api';
import { fetchReport } from '../../../services/reportsApi';
import type { ByChannelType, OrderChannelGroup, RefundLogFilterKind } from '../../../interface/clickHouseInterface';

const REPORTS_BASE_URL = process.env.REACT_APP_API_ENDPOINT_REPORTS || '';

// Global sales-channel filter value forwarded as the `orderSource` query param.
// Omit entirely for "Overall sales" — only pass for Direct / 3rd party.
export type OrderSource = 'direct' | 'thirdparty';

export const clickHouseApi = (locationId: string) => {
  return REVAMP_API({
    method: 'get',
    url: `/api/v1/items/${locationId}`,
  });
};

export const getSnapshotDetails = ({ locationId, startDate, endDate, orderSource, token }: { locationId: string; startDate?: string; endDate?: string; orderSource?: OrderSource; token?: string | null }) => {
  const params = new URLSearchParams({ locationId });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (orderSource) params.set('orderSource', orderSource);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/snapshot-details?${params}`, token);
};

export const getLiveItemSalesV2Api = ({ locationId, page = 0, size = 10, token, pinnedItemNames, startDate, endDate, orderSource }: { locationId: string; page?: number; size?: number; token?: string | null; pinnedItemNames?: string[]; startDate?: string; endDate?: string; orderSource?: OrderSource }) => {
  const params = new URLSearchParams({ locationId, page: String(page), size: String(size) });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (pinnedItemNames?.length) pinnedItemNames.forEach((n) => params.append('pinnedItemNames', n));
  if (orderSource) params.set('orderSource', orderSource);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/item-sales?${params}`, token);
};

// ZAR-2371 — also takes the FE-resolved comparison window (compStartDate/compEndDate, same
// contract as snapshot-details/comparison) for the per-row netSalesDelta, and `type=source`
// for order-source rows instead of channel rows (omitted for channels).
export const getLiveByChannelApi = ({ locationId, startDate, endDate, orderSource, compStartDate, compEndDate, type, token }: { locationId: string; startDate?: string; endDate?: string; orderSource?: OrderSource; compStartDate?: string; compEndDate?: string; type?: ByChannelType; token?: string | null }) => {
  const params = new URLSearchParams({ locationId });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (orderSource) params.set('orderSource', orderSource);
  if (compStartDate) params.set('compStartDate', compStartDate);
  if (compEndDate) params.set('compEndDate', compEndDate);
  if (type) params.set('type', type);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/by-channel?${params}`, token);
};

export const getLivePaymentsApi = ({ locationId, startDate, endDate, orderSource, token }: { locationId: string; startDate?: string; endDate?: string; orderSource?: OrderSource; token?: string | null }) => {
  const params = new URLSearchParams({ locationId });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (orderSource) params.set('orderSource', orderSource);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/payments?${params}`, token);
};

export const getLiveRefundSummaryApi = ({ locationId, startDate, endDate, orderSource, token }: { locationId: string; startDate?: string; endDate?: string; orderSource?: OrderSource; token?: string | null }) => {
  const params = new URLSearchParams({ locationId });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (orderSource) params.set('orderSource', orderSource);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/refund-summary?${params}`, token);
};

export const getLiveRefundDetailsApi = ({ locationId, page, size, startDate, endDate, orderSource, token }: { locationId: string; page: number; size: number; startDate?: string; endDate?: string; orderSource?: OrderSource; token?: string | null }) => {
  const params = new URLSearchParams({ locationId, page: String(page), size: String(size) });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (orderSource) params.set('orderSource', orderSource);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/refund-details?${params}`, token);
};

// REF-2 — the Refund Log's real data source. `size` defaults large for the whole-period,
// unfiltered fetch (RefundsDrillPage's `records` — feeds the context strip and the Refund
// Reasons / Refunds by Staff cards). The Refund Log table's own fetch (useRefundLogTableQuery)
// calls this same function again with `search`/`type`/`reason`/`staff`/`channel`/`sortBy`/`sortDir`
// so its search, sort and Type/Reason/Staff/Channel filters run server-side.
export const getRefundLogApi = ({ locationId, startDate, endDate, orderSource, page = 1, size = 500, search, type, reason, staff, channel, sortBy, sortDir, token }: {
  locationId: string; startDate?: string; endDate?: string; orderSource?: OrderSource; page?: number; size?: number;
  /** Order no., reason, staff, guest or channel. */
  search?: string;
  /** ONE `type` param, values comma-joined (sent as `type=Fee,Full`, not repeated keys) —
   * unlike reason/staff/channel below. Matches the `refundType` response field
   * ('Full' | 'Item' | 'Fee'). */
  type?: string[];
  /** Repeated per value — matches `reason` exactly (literal text, e.g. 'Customer Request'). */
  reason?: string[];
  /** Repeated per value — matches `staffName` exactly. */
  staff?: string[];
  /** Repeated per value — matches `channel` exactly. */
  channel?: string[];
  /** Refund-log response field to sort on, e.g. 'refundDate', 'refunded'. */
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  token?: string | null;
}) => {
  const params = new URLSearchParams({ locationId, page: String(page), size: String(size) });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (orderSource) params.set('orderSource', orderSource);
  if (search) params.set('search', search);
  if (type?.length) params.set('type', type.join(','));
  (reason ?? []).forEach((v) => params.append('reason', v));
  (staff ?? []).forEach((v) => params.append('staff', v));
  (channel ?? []).forEach((v) => params.append('channel', v));
  if (sortBy) params.set('sortBy', sortBy);
  if (sortDir) params.set('sortDir', sortDir);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/refund-log?${params}`, token);
};

// REF-2 — the Refund Log filter dropdowns' real data source. NOT YET DEPLOYED (per product,
// 2026-09-23) — wired now so the dropdowns light up the moment it ships; until then every
// menu will show its own failure/retry state (see useRefundLogFilterOptions.ts).
export const getRefundLogFiltersApi = ({ locationId, type, startDate, endDate, orderSource, token }: {
  locationId: string;
  /** Which dropdown is asking. 'type' is a fixed ['Full','Item','Fee']; 'reason'/'staff'/'channel' are period-scoped. */
  type: RefundLogFilterKind;
  startDate?: string;
  endDate?: string;
  orderSource?: OrderSource;
  token?: string | null;
}) => {
  const params = new URLSearchParams({ locationId, type });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (orderSource) params.set('orderSource', orderSource);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/refund-log-filters?${params}`, token);
};

// REF-6 — the Refund Reasons card's real data source.
export const getRefundReasonsApi = ({ locationId, startDate, endDate, orderSource, page, size, token }: { locationId: string; startDate?: string; endDate?: string; orderSource?: OrderSource; /** 1-indexed; server default 1. */ page?: number; /** Server default 10. */ size?: number; token?: string | null }) => {
  const params = new URLSearchParams({ locationId });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (orderSource) params.set('orderSource', orderSource);
  if (page != null) params.set('page', String(page));
  if (size != null) params.set('size', String(size));
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/refund-reasons?${params}`, token);
};

// REF-6 — the Refunds by Staff card's real data source.
export const getRefundByStaffApi = ({ locationId, startDate, endDate, orderSource, page, size, token }: { locationId: string; startDate?: string; endDate?: string; orderSource?: OrderSource; /** 1-indexed; server default 1. */ page?: number; /** Server default 10. */ size?: number; token?: string | null }) => {
  const params = new URLSearchParams({ locationId });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (orderSource) params.set('orderSource', orderSource);
  if (page != null) params.set('page', String(page));
  if (size != null) params.set('size', String(size));
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/refund-by-staff?${params}`, token);
};

// REF-1 — the Component Split card's real data source.
export const getRefundComponentSplitApi = ({ locationId, startDate, endDate, orderSource, token }: { locationId: string; startDate?: string; endDate?: string; orderSource?: OrderSource; token?: string | null }) => {
  const params = new URLSearchParams({ locationId });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (orderSource) params.set('orderSource', orderSource);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/refund-component-split?${params}`, token);
};

// `compStartDate`/`compEndDate` — the FE-resolved comparison window (same contract as
// getLiveByChannelApi) — let the backend compute each row's `delta`, which feeds the
// Voided Items stamp KPI's percent-change arrow.
export const getLiveVoidSummaryApi = (locationId: string, token?: string | null, startDate?: string, endDate?: string, orderSource?: OrderSource, compStartDate?: string, compEndDate?: string) => {
  const params = new URLSearchParams({ locationId });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (orderSource) params.set('orderSource', orderSource);
  if (compStartDate) params.set('compStartDate', compStartDate);
  if (compEndDate) params.set('compEndDate', compEndDate);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/void-summary?${params}`, token);
};

// Voids by Staff ranked bars' real data source.
export const getVoidByStaffApi = ({ locationId, startDate, endDate, orderSource, token }: { locationId: string; startDate?: string; endDate?: string; orderSource?: OrderSource; token?: string | null }) => {
  const params = new URLSearchParams({ locationId });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (orderSource) params.set('orderSource', orderSource);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/void-staff?${params}`, token);
};

// The Void drill-in's Mini-KPIs row (Voided Items · Void Value · Void Rate · Avg Void Value).
export const getVoidKpisApi = ({ locationId, startDate, endDate, orderSource, token }: { locationId: string; startDate?: string; endDate?: string; orderSource?: OrderSource; token?: string | null }) => {
  const params = new URLSearchParams({ locationId });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (orderSource) params.set('orderSource', orderSource);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/void-kpis?${params}`, token);
};

export const getLiveVoidDetailsApi = ({ locationId, page, size, token, startDate, endDate, orderSource }: { locationId: string; page: number; size: number; token?: string | null; startDate?: string; endDate?: string; orderSource?: OrderSource }) => {
  const params = new URLSearchParams({ locationId, page: String(page), size: String(size) });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (orderSource) params.set('orderSource', orderSource);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/paid-void-details?${params}`, token);
};

export const getLiveHourlySalesApi = (locationId: string, token?: string | null, orderSource?: OrderSource) => {
  const params = new URLSearchParams({ locationId });
  if (orderSource) params.set('orderSource', orderSource);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/hourly-sales?${params}`, token);
};

export const getHourlySalesApi = ({ locationId, startDate, endDate, orderSource, token }: { locationId: string; startDate: string; endDate: string; orderSource?: OrderSource; token?: string | null }) => {
  const params = new URLSearchParams({ locationId, startDate, endDate });
  if (orderSource) params.set('orderSource', orderSource);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/hourly-sales?${params}`, token);
};

export const getLiveStaffPerformanceApi = ({ locationId, startDate, endDate, orderSource, token }: { locationId: string; startDate?: string; endDate?: string; orderSource?: OrderSource; token?: string | null }) => {
  const params = new URLSearchParams({ locationId });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (orderSource) params.set('orderSource', orderSource);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/staff-perfomance?${params}`, token);
};

export const getLiveStaffActiveClockingApi = ({ locationId, startDate, endDate, orderSource, token }: { locationId: string; startDate?: string; endDate?: string; orderSource?: OrderSource; token?: string | null }) => {
  const params = new URLSearchParams({ locationId });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (orderSource) params.set('orderSource', orderSource);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/staff-active-clocking?${params}`, token);
};

// Staff Roster (paginated/searchable/sortable, any date range) — GET /sales/live/v2/staff-active-clocking-v2
export const getLiveStaffActiveClockingV2Api = ({ locationId, startDate, endDate, search = '', page = 1, size = 10, sortBy, sortAsc, orderSource, token }: { locationId: string; startDate?: string; endDate?: string; search?: string; page?: number; size?: number; sortBy?: string; sortAsc?: boolean; orderSource?: OrderSource; token?: string | null }) => {
  const params = new URLSearchParams({ locationId, page: String(page), size: String(size), search });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (sortBy) params.set('sortBy', sortBy);
  if (sortAsc !== undefined) params.set('sortAsc', String(sortAsc));
  if (orderSource) params.set('orderSource', orderSource);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/staff-active-clocking-v2?${params}`, token);
};

export const getOrderLogApi = ({
  locationId,
  page,
  size,
  sortBy,
  sortDir,
  search,
  channelName,
  channelGroup,
  guestCount,
  minAmount,
  maxAmount,
  startDate,
  endDate,
  orderSource,
  type,
}: {
  locationId: string;
  page: number;
  size: number;
  sortBy: string;
  sortDir: string;
  search?: string;
  channelName?: string;
  /** Sales-channel filter used by the Sales by Channel breakdown drill-down. */
  channelGroup?: OrderChannelGroup;
  guestCount?: number;
  minAmount?: number;
  maxAmount?: number;
  startDate?: string;
  endDate?: string;
  orderSource?: OrderSource;
  /** Narrows the log to a subset of orders — 'cancel' returns cancelled orders (CANC-1). */
  type?: string;
}) => {
  const params = new URLSearchParams({ locationId, page: String(page), size: String(size), sortBy, sortDir });
  if (type) params.set('type', type);
  if (search) params.set('search', search);
  if (channelGroup) params.set('channelGroup', channelGroup);
  if (guestCount !== undefined) params.set('guestCount', String(guestCount));
  if (minAmount !== undefined) params.set('minAmount', String(minAmount));
  if (maxAmount !== undefined) params.set('maxAmount', String(maxAmount));
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (orderSource) params.set('orderSource', orderSource);
  const channelSuffix = channelName ? `&channelName=${channelName}` : '';
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/order-log?${params}${channelSuffix}`);
};

// Channel Names — GET /sales/live/v2/channel-names
export const getChannelNamesApi = (locationId: string) => {
  const params = new URLSearchParams({ locationId });
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/channel-names?${params}`);
};

// Same endpoint without pagination — used for CSV / PDF export
export const getOrderLogExportApi = ({
  locationId,
  page,
  size,
  sortBy,
  sortDir,
  search,
  channelName,
  channelGroup,
  guestCount,
  minAmount,
  maxAmount,
  startDate,
  endDate,
  orderSource,
  type,
}: {
  locationId: string;
  page: number;
  size: number;
  sortBy: string;
  sortDir: string;
  search?: string;
  channelName?: string;
  /** Sales-channel filter used by the Sales by Channel breakdown drill-down. */
  channelGroup?: OrderChannelGroup;
  guestCount?: number;
  minAmount?: number;
  maxAmount?: number;
  startDate?: string;
  endDate?: string;
  orderSource?: OrderSource;
  /** Narrows the log to a subset of orders — 'cancel' returns cancelled orders (CANC-1). */
  type?: string;
}) => {
  const params = new URLSearchParams({ locationId, page: String(page), size: String(size), sortBy, sortDir });
  if (type) params.set('type', type);
  if (search) params.set('search', search);
  if (channelGroup) params.set('channelGroup', channelGroup);
  if (guestCount !== undefined) params.set('guestCount', String(guestCount));
  if (minAmount !== undefined) params.set('minAmount', String(minAmount));
  if (maxAmount !== undefined) params.set('maxAmount', String(maxAmount));
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (orderSource) params.set('orderSource', orderSource);
  const channelSuffix = channelName ? `&channelName=${channelName}` : '';
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/order-log?${params}${channelSuffix}`);
};

// Order Detail — GET /sales/live/v2/order-detail
export const getOrderDetailApi = ({
  locationId,
  orderId,
  type,
}: {
  locationId: string;
  orderId: string;
  type?: 'historical';
}) => {
  const params = new URLSearchParams({ locationId, orderId });
  if (type) params.set('type', type);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/order-detail?${params}`);
};

// Unpaid Orders — GET /sales/live/v2/unpaid-orders
export const getLiveUnpaidOrdersApi = ({
  locationId,
  page,
  size,
  startDate,
  endDate,
  sortBy,
  sortDir,
  search,
  orderType,
  orderSource,
}: {
  locationId: string;
  page?: number;
  size?: number;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  search?: string;
  orderType?: string;
  orderSource?: OrderSource;
}) => {
  const params = new URLSearchParams({ locationId });
  if (page !== undefined) params.set('page', String(page));
  if (size !== undefined) params.set('size', String(size));
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (sortBy) params.set('sortBy', sortBy);
  if (sortDir) params.set('sortDir', sortDir);
  if (search) params.set('search', search);
  if (orderType) params.set('orderType', orderType);
  if (orderSource) params.set('orderSource', orderSource);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/unpaid-orders?${params}`);
};

// Comp Order Log — GET /sales/live/v2/comp-order-log
export const getLiveCompOrderLogApi = ({
  locationId,
  page,
  size,
  startDate,
  endDate,
  orderSource,
}: {
  locationId: string;
  page: number;
  size: number;
  startDate?: string;
  endDate?: string;
  orderSource?: OrderSource;
}) => {
  const params = new URLSearchParams({ locationId, page: String(page), size: String(size) });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (orderSource) params.set('orderSource', orderSource);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/comp-order-log?${params}`);
};

// Comp Reasons — GET /sales/live/v2/comp-reasons
export const getLiveCompReasonsApi = ({ locationId, startDate, endDate, orderSource }: { locationId: string; startDate?: string; endDate?: string; orderSource?: OrderSource }) => {
  const params = new URLSearchParams({ locationId });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (orderSource) params.set('orderSource', orderSource);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/comp-reasons?${params}`);
};

// Comp by Staff — GET /sales/live/v2/comp-by-staff
export const getLiveCompByStaffApi = ({ locationId, startDate, endDate, orderSource }: { locationId: string; startDate?: string; endDate?: string; orderSource?: OrderSource }) => {
  const params = new URLSearchParams({ locationId });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (orderSource) params.set('orderSource', orderSource);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/comp-by-staff?${params}`);
};

// Sales by Category — GET /sales/live/v2/categoy-sales
export const getLiveCategorySalesApi = ({ locationId, page, size, startDate, endDate, orderSource, token }: { locationId: string; page?: number; size?: number; startDate?: string; endDate?: string; orderSource?: OrderSource; token?: string | null }) => {
  const params = new URLSearchParams({ locationId });
  if (page != null) params.set('page', String(page));
  if (size != null) params.set('size', String(size));
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (orderSource) params.set('orderSource', orderSource);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/categoy-sales?${params}`, token);
};

// Staff Refunds — GET /sales/live/v2/staff-refunds
export const getLiveStaffRefundsApi = ({ locationId, startDate, endDate, orderSource, token }: { locationId: string; startDate?: string; endDate?: string; orderSource?: OrderSource; token?: string | null }) => {
  const params = new URLSearchParams({ locationId });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (orderSource) params.set('orderSource', orderSource);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/staff-refunds?${params}`, token);
};

// Snapshot Comparison — GET /sales/live/v2/snapshot-details/comparison
//
// ZAR-1661: the comparison window travels with the request as `compStartDate` /
// `compEndDate`, resolved FE-side from the SHAPE of the selection. This function stays
// dumb about how they are derived — `fetchSnapshotComparisonSaga` resolves them through
// `utils/reportPeriod.comparisonQueryParams` before calling. The live "Today" request
// sends no startDate/endDate and only `compStartDate`; historical requests send both.
export const getSnapshotComparison = ({ locationId, startDate, endDate, compStartDate, compEndDate, orderSource, token }: { locationId: string; startDate?: string; endDate?: string; compStartDate?: string; compEndDate?: string; orderSource?: OrderSource; token?: string | null }) => {
  const params = new URLSearchParams({ locationId });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (compStartDate) params.set('compStartDate', compStartDate);
  if (compEndDate) params.set('compEndDate', compEndDate);
  if (orderSource) params.set('orderSource', orderSource);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/snapshot-details/comparison?${params}`, token);
};

// Outstanding Value — GET /sales/live/v2/outstanding-value
export const getLiveOutstandingValueApi = ({ locationId, startDate, endDate, orderSource, token }: { locationId: string; startDate?: string; endDate?: string; orderSource?: OrderSource; token?: string | null }) => {
  const params = new URLSearchParams({ locationId });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (orderSource) params.set('orderSource', orderSource);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/outstanding-value?${params}`, token);
};

// Labour Employee Sales (KPI) — GET /sales/live/v2/labour-employee-sales
export const getLabourEmployeeSalesApi = ({ locationId, startDate, endDate, employeeId }: { locationId: string; startDate?: string; endDate?: string; employeeId?: string }) => {
  const params = new URLSearchParams({ locationId });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (employeeId) params.set('employeeId', employeeId);
  return fetchReport(`${REPORTS_BASE_URL}/sales/labour/employee-sales?${params}`);
};

// Labour Tips — GET /sales/labour/tips
export const getLabourTipsApi = ({ locationId, startDate, endDate, employeeId }: { locationId: string; startDate?: string; endDate?: string; employeeId?: string }) => {
  const params = new URLSearchParams({ locationId });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (employeeId) params.set('employeeId', employeeId);
  return fetchReport(`${REPORTS_BASE_URL}/sales/labour/tips?${params}`);
};

// Labour Voids — GET /sales/labour/voids
export const getLabourVoidsApi = ({ locationId, startDate, endDate, employeeId }: { locationId: string; startDate?: string; endDate?: string; employeeId?: string }) => {
  const params = new URLSearchParams({ locationId });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (employeeId) params.set('employeeId', employeeId);
  return fetchReport(`${REPORTS_BASE_URL}/sales/labour/voids?${params}`);
};

// Labour Working Hours (KPI) — GET /sales/labour/working-hours
export const getLabourWorkingHoursApi = ({ locationId, startDate, endDate, employeeId }: { locationId: string; startDate?: string; endDate?: string; employeeId?: string }) => {
  const params = new URLSearchParams({ locationId });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (employeeId) params.set('employeeId', employeeId);
  return fetchReport(`${REPORTS_BASE_URL}/sales/labour/working-hours?${params}`);
};

// Labour Staff Performance — GET /sales/labour/staff-performance
export const getLabourStaffPerformanceApi = ({ locationId, page = 1, size = 10, search = '', startDate, endDate, employeeId, sortBy, sortAsc }: { locationId: string; page?: number; size?: number; search?: string; startDate?: string; endDate?: string; employeeId?: string; sortBy?: string; sortAsc?: boolean }) => {
  const params = new URLSearchParams({ locationId, page: String(page), size: String(size), search });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (employeeId) params.set('employeeId', employeeId);
  if (sortBy) params.set('sortBy', sortBy);
  if (sortAsc !== undefined) params.set('sortAsc', String(sortAsc));
  return fetchReport(`${REPORTS_BASE_URL}/sales/labour/staff-performance?${params}`);
};

// Labour Working Hours Log — GET /sales/labour/working-hours-log
export const getLabourWorkingHoursLogApi = ({ locationId, page = 1, size = 10, search = '', startDate, endDate, employeeId, sortBy, sortAsc }: { locationId: string; page?: number; size?: number; search?: string; startDate?: string; endDate?: string; employeeId?: string; sortBy?: string; sortAsc?: boolean }) => {
  const params = new URLSearchParams({ locationId, page: String(page), size: String(size), search });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (employeeId) params.set('employeeId', employeeId);
  if (sortBy) params.set('sortBy', sortBy);
  if (sortAsc !== undefined) params.set('sortAsc', String(sortAsc));
  return fetchReport(`${REPORTS_BASE_URL}/sales/labour/working-hours-log?${params}`);
};

// Labour KPI Comparison — GET /sales/labour/kpi-comparison
//
// ZAR-1661: carries the FE-derived comparison window, exactly as the snapshot comparison
// endpoint does — `fetchLabourKpiComparisonSaga` resolves the params through
// `utils/reportPeriod.comparisonQueryParams`. Today sends `compStartDate` alone;
// historical sends both.
export const getLabourKpiComparisonApi = ({ locationId, startDate, endDate, compStartDate, compEndDate, employeeId }: { locationId: string; startDate?: string; endDate?: string; compStartDate?: string; compEndDate?: string; employeeId?: string }) => {
  const params = new URLSearchParams({ locationId });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (compStartDate) params.set('compStartDate', compStartDate);
  if (compEndDate) params.set('compEndDate', compEndDate);
  if (employeeId) params.set('employeeId', employeeId);
  return fetchReport(`${REPORTS_BASE_URL}/sales/labour/kpi-comparison?${params}`);
};

// Labour Employees — GET /sales/labour/employees
export const getLabourEmployeesApi = ({ locationId }: { locationId: string }) => {
  const params = new URLSearchParams({ locationId });
  return fetchReport(`${REPORTS_BASE_URL}/sales/labour/employees?${params}`);
};

// Sales by Channel — Financial Breakdown (ZAR-1555) — GET /sales/live/v2/sales-by-channel-breakdown
// Today omits startDate/endDate (live path); historical ranges pass both.
export const getSalesByChannelBreakdownApi = ({ locationId, startDate, endDate, orderSource, token }: { locationId: string; startDate?: string; endDate?: string; orderSource?: OrderSource; token?: string | null }) => {
  const params = new URLSearchParams({ locationId });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (orderSource) params.set('orderSource', orderSource);
  return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/sales-by-channel-breakdown?${params}`, token);
};
